import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserService } from '../../../../../service/UserService';
import { useAuthStore } from '../../../../../store/storeAuth';
import useFlowsStore from '../../../../../store/storeFlows';
import {
  NotificationType,
  useNotificationStore,
} from '../../../../../store/storeNotifications';
import { useObjectStore } from '../../../../../store/storeObjects';
import { CreateItemType, SpaceOption } from '../../types';
import { ensureReservedFolder } from '../../util/ensureReservedFolder';

interface UseCreateItemModalOptions {
  initialFolderId?: string;
  initialSpaceId?: string;
}

export const useCreateItemModal = (
  type: CreateItemType,
  onHide: () => void,
  options?: UseCreateItemModalOptions,
) => {
  const navigate = useNavigate();
  const { setAuthUser, setFolders } = useAuthStore(state => state);
  const { setObjectUpdate } = useObjectStore(state => state);
  const { addFlow } = useFlowsStore(state => state);
  const setNotification = useNotificationStore(state => state.setNotification);

  const initialFolderId = options?.initialFolderId;
  const initialSpaceId = options?.initialSpaceId;

  const [name, setName] = useState('');
  const [selectedSpaceId, setSelectedSpaceId] = useState<string>(
    initialSpaceId ?? 'private',
  );
  const [spaces, setSpaces] = useState<SpaceOption[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [showVectorModal, setShowVectorModal] = useState(false);
  const [showObjectModal, setShowObjectModal] = useState(false);
  const [createdObjectSpaceId, setCreatedObjectSpaceId] =
    useState<string>('user');
  const [vectorMemorySpaceId, setVectorMemorySpaceId] = useState<
    string | undefined
  >(undefined);
  const [showInterfaceModal, setShowInterfaceModal] = useState(false);
  const [isCreateFormLoading, setIsCreateFormLoading] = useState(false);
  const [expandCreateWithAi, setExpandCreateWithAi] = useState(false);
  const [userPrompt, setUserPrompt] = useState('');
  const [isCreatingFromShorthand, setIsCreatingFromShorthand] = useState(false);

  useEffect(() => {
    loadSpaces();
  }, []);

  const loadSpaces = async () => {
    try {
      const { data } = await UserService.getSpaces();
      setSpaces(data);
    } catch (error) {
      console.error('Failed to load spaces:', error);
    }
  };

  const ensureFolderExists = async (
    folderName: string,
    spaceId?: string,
  ): Promise<string | null> => {
    const folderId = await ensureReservedFolder(folderName, spaceId, {
      onPrivateFoldersUpdated: privateFolders => setFolders(privateFolders),
      onSpacesUpdated: updatedSpaces => setSpaces(updatedSpaces),
    });
    if (folderId === null) {
      setNotification({
        type: NotificationType.Error,
        message: `Failed to create folder ${folderName}`,
      });
    }
    return folderId;
  };

  const resetForm = () => {
    setName('');
    setSelectedSpaceId(initialSpaceId ?? 'private');
    setUserPrompt('');
    setExpandCreateWithAi(false);
  };

  return {
    name,
    setName,
    selectedSpaceId,
    setSelectedSpaceId,
    spaces,
    isCreating,
    setIsCreating,
    showVectorModal,
    setShowVectorModal,
    showObjectModal,
    setShowObjectModal,
    createdObjectSpaceId,
    setCreatedObjectSpaceId,
    vectorMemorySpaceId,
    setVectorMemorySpaceId,
    showInterfaceModal,
    setShowInterfaceModal,
    isCreateFormLoading,
    setIsCreateFormLoading,
    expandCreateWithAi,
    setExpandCreateWithAi,
    userPrompt,
    setUserPrompt,
    isCreatingFromShorthand,
    setIsCreatingFromShorthand,
    ensureFolderExists,
    loadSpaces,
    resetForm,
    navigate,
    setAuthUser,
    addFlow,
    setNotification,
    setObjectUpdate,
    initialFolderId,
    initialSpaceId,
  };
};
